import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';

export type DelegatedSession = { kind: 'delegated'; principal: { userId: string; householdId: string; scopes: string[]; authKind: 'delegated_browser' } };
export type AuthSession = User | DelegatedSession;
export const isDelegated = (user: AuthSession | null): user is DelegatedSession => user !== null && 'kind' in user && user.kind === 'delegated';
const delegatedMode = () => sessionStorage.getItem('rec-auth-mode') === 'delegated';
const sealedError = () => new Error('Die delegierte Sitzung ist abgelaufen. Öffne einen neuen Handoff über den ursprünglichen Calculator-Zugang.');

function base64url(value: Uint8Array): string {
  return btoa(String.fromCharCode(...value)).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
}
async function bootstrapDelegated() {
  // Erase the one-use ticket before the first wait. No source token, fabricated
  // OIDC User or refresh credential ever enters browser storage.
  const ticket = new URLSearchParams(location.hash.slice(1)).get('ticket');
  history.replaceState({}, '', '/auth/delegated/start');
  sessionStorage.setItem('rec-auth-mode', 'delegated');
  if (!ticket || !/^[A-Za-z0-9_-]{43}$/.test(ticket)) throw sealedError();
  const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
  const state = base64url(crypto.getRandomValues(new Uint8Array(32)));
  const challenge = base64url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
  const send = (path: string, body: object) => fetch(path, { method: 'POST', credentials: 'same-origin', redirect: 'error', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const begin = await send('/auth/delegated/begin', { ticket, state, codeChallenge: challenge });
  if (!begin.ok) throw sealedError();
  const { code } = await begin.json() as { code: string };
  const complete = await send('/auth/delegated/complete', { code, state, codeVerifier: verifier });
  if (!complete.ok) throw sealedError();
  history.replaceState({}, '', '/properties');
}

type AuthConfig = { issuer?: string; clientId?: string; scope: string };
let managerPromise: Promise<UserManager> | null = null;

async function manager(): Promise<UserManager> {
  if (!managerPromise) managerPromise = fetch('/auth/config').then(async response => {
    if (!response.ok) throw new Error('Login configuration unavailable');
    const config = await response.json() as AuthConfig;
    if (!config.issuer || !config.clientId) throw new Error('Web login is not configured');
    return new UserManager({
      authority: config.issuer,
      client_id: config.clientId,
      redirect_uri: `${location.origin}/auth/callback`,
      post_logout_redirect_uri: `${location.origin}/auth/logout/callback`,
      response_type: 'code',
      scope: config.scope,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      automaticSilentRenew: false
    });
  });
  return managerPromise;
}

export async function currentUser(): Promise<AuthSession | null> {
  if (location.pathname === '/auth/delegated/start') await bootstrapDelegated();
  const delegated = await fetch('/auth/delegated/me', { credentials: 'same-origin', redirect: 'error' });
  if (delegated.ok) {
    sessionStorage.setItem('rec-auth-mode', 'delegated');
    return { kind: 'delegated', principal: await delegated.json() as DelegatedSession['principal'] };
  }
  // An expired/revoked delegation never silently becomes an ordinary SSO login,
  // even when oidc-client has a still-valid user or refresh token in localStorage.
  if (delegatedMode() || delegated.headers.get('X-Delegated-Session') === 'present') {
    sessionStorage.setItem('rec-auth-mode', 'delegated');
    throw sealedError();
  }
  const m = await manager();
  if (location.pathname === '/auth/logout/callback') {
    if (new URL(location.href).searchParams.has('state')) await m.signoutRedirectCallback();
    history.replaceState({}, '', '/');
  }
  if (location.pathname === '/auth/callback') {
    const user = await m.signinRedirectCallback();
    history.replaceState({}, '', '/');
    return user;
  }
  const user = await m.getUser();
  if (user && user.expired && user.refresh_token) return m.signinSilent().catch(() => null);
  return user?.expired ? null : user;
}
export async function signIn() { if (delegatedMode()) throw sealedError(); return (await manager()).signinRedirect(); }
export async function signOut() {
  if (delegatedMode()) {
    const response = await fetch('/auth/delegated/logout', { method: 'POST', credentials: 'same-origin', redirect: 'error' });
    if (!response.ok) throw sealedError();
    // Preserve delegated mode after logout: ordinary OIDC storage stays irrelevant.
    location.assign('/auth/delegated/closed');
    return;
  }
  return (await manager()).signoutRedirect();
}
export async function authFetch(user: AuthSession, path: string, init: RequestInit = {}, format: 'json' | 'blob' | 'response' = 'json') {
  if (isDelegated(user)) {
    const url = new URL(path, location.origin);
    if (url.origin !== location.origin || !url.pathname.startsWith('/api/')) throw sealedError();
    const headers = new Headers(init.headers);
    headers.delete('Authorization');
    if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    const response = await fetch(url.pathname + url.search, { ...init, headers, credentials: 'same-origin', redirect: 'error' });
    if (response.status === 401 || response.status === 403) throw sealedError();
    if (!response.ok) {
      const body = await response.json().catch(() => null) as { error?: string } | null;
      throw new Error(body?.error ?? `Request failed (${response.status})`);
    }
    return format === 'response' ? response : response.status === 204 ? null : format === 'blob' ? response.blob() : response.json();
  }
  const send = (accessToken: string) => {
    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${accessToken}`);
    if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    return fetch(path, {...init, headers});
  };

  // A refreshed token may replace the caller's token only within the same
  // issuer/subject. Storage removal or another account cannot retarget a stale
  // page's reads, writes, retries or private response delivery.
  const issuer = user.profile?.iss, subject = user.profile?.sub;
  const changed = () => new Error('Anmeldung hat gewechselt oder wurde beendet. Seite neu laden. Bei einem bereits gesendeten Auftrag dessen ursprünglichen Stand prüfen.');
  const checked = (current: User | null): User => {
    if (delegatedMode()) throw sealedError();
    if (typeof issuer !== 'string' || !issuer || typeof subject !== 'string' || !subject
      || !current || current.profile?.iss !== issuer || current.profile?.sub !== subject
      || typeof current.access_token !== 'string' || !current.access_token) throw changed();
    return current;
  };
  if (delegatedMode()) throw sealedError();
  if (typeof issuer !== 'string' || !issuer || typeof subject !== 'string' || !subject) throw changed();
  const m = await manager();
  const stored = async () => checked(await m.getUser());
  let activeUser = await stored();
  if (activeUser.expired && activeUser.refresh_token) {
    checked(await m.signinSilent());
    activeUser = await stored();
  }
  if (activeUser.expired) throw new Error('Deine Sitzung ist abgelaufen. Melde dich erneut an.');
  let response = await send(activeUser.access_token);
  await stored();
  if (response.status === 401 && activeUser.refresh_token) {
    checked(await m.signinSilent());
    activeUser = await stored();
    if (activeUser.expired) throw new Error('Deine Sitzung ist abgelaufen. Melde dich erneut an.');
    response = await send(activeUser.access_token);
    await stored();
  }
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: string; detail?: string } | null;
    await stored();
    const detail = payload?.error ?? payload?.detail;
    throw new Error(detail ? `${detail} (${response.status})` : `Request failed (${response.status})`);
  }
  const value = format === 'response' ? response : response.status === 204 ? null : format === 'blob' ? await response.blob() : await response.json();
  await stored();
  return value;
}
